import { useRef, useState } from 'react';
import Calendar from 'react-calendar';
import { DateTime } from 'luxon';

import { calendarFormatProps } from '../CalendarProps';
import { cn } from '../cn';
import type { ColorName, ColorVariant, Style } from './buttonClasses';
import { composeVariant } from './buttonClasses';
import type { Size } from './types';
import { sizeRounded } from './types';

export type CalendarPopoverProps = {
  popoverId: string;
  anchorName: string;
  size?: Size;
  /** Color axis — `'blue' | 'red' | 'green' | …`. Defaults to `'gray'`. */
  color?: ColorName;
  /** Style axis — `'filled' | 'outline' | 'soft'`. Defaults to `'filled'`. */
  tone?: Style;
  value?: number; // Timestamp in ms
  onDateChange?: (date: string) => void; // "yyyy-MM-dd" format
  onOpenChange?: (open: boolean) => void;
  minDate?: Date; // Days before this can't be picked
  maxDate?: Date; // Days after this can't be picked
};

export function popoverBorder(variant: ColorVariant = 'gray') {
  const v = variant.endsWith('Outline') ? variant.replace('Outline', '') : variant;
  switch (v) {
    case 'red':
      return 'border-fair-pink-200 dark:border-fair-pink-700';
    case 'green':
      return 'border-mantis-200 dark:border-mantis-700';
    case 'yellow':
      return 'border-quarter-spanish-white-200 dark:border-quarter-spanish-white-700';
    case 'purple':
      return 'border-purple-200 dark:border-purple-700';
    case 'blue':
      return 'border-astronaut-200 dark:border-astronaut-700';
    default:
      return 'border-slate-200 dark:border-charade-700';
  }
}

export default function CalendarPopover({
  popoverId,
  anchorName,
  size = 'md',
  color,
  tone,
  value,
  onDateChange,
  onOpenChange,
  minDate,
  maxDate,
}: CalendarPopoverProps) {
  const effectiveVariant: ColorVariant = composeVariant(color ?? 'gray', tone ?? 'filled');
  const [isOpen, setIsOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);

  const valueDate = value && !isNaN(value) ? new Date(value) : undefined;

  // Fade the leading/trailing days that belong to the adjacent month. calendar.css already
  // has a `text-slate-400` rule for these, but it loses to the equal-specificity
  // `.react-calendar__tile` color that comes later in the file. `#popoverId` (scoped to this
  // popover) raises specificity above both so the fade wins. slate-400 / dark:slate-600.
  const neighborFadeCss = `
    #${popoverId} .react-calendar__month-view__days__day--neighboringMonth{color:#94a3b8!important}
    .dark #${popoverId} .react-calendar__month-view__days__day--neighboringMonth{color:#475569!important}
  `;

  function handleDateChange(date: Date | null) {
    if (date && onDateChange) {
      const dateStr = DateTime.fromJSDate(date).toFormat('yyyy-MM-dd');
      onDateChange(dateStr);
      popoverRef.current?.hidePopover();
    }
  }

  return (
    <div
      ref={popoverRef}
      id={popoverId}
      popover="auto"
      onToggle={(e) => {
        const open = (e as React.ToggleEvent<HTMLDivElement>).newState === 'open';
        setIsOpen(open);
        onOpenChange?.(open);
      }}
      style={{
        position: 'fixed',
        positionAnchor: anchorName,
        positionArea: 'block-end span-inline-end',
        positionTryFallbacks: 'flip-block, flip-inline, flip-block flip-inline',
        justifySelf: 'start',
        margin: 0,
        inset: 'auto',
      } as unknown as React.CSSProperties}
      className={cn(
        'p-0 border bg-white dark:bg-charade-950 shadow-lg',
        popoverBorder(effectiveVariant),
        sizeRounded(size),
        !isOpen && 'hidden',
      )}>
      <style>{neighborFadeCss}</style>
      <Calendar
        value={valueDate}
        {...calendarFormatProps}
        minDate={minDate}
        maxDate={maxDate}
        onChange={(date) => {
          if (date instanceof Date) {
            handleDateChange(date);
          }
        }}
      />
    </div>
  );
}
